/**
 * Client Hints仕様の一部: 2025/03現在はChromiumベースのブラウザでのみ実装されている
 * https://developer.mozilla.org/ja/docs/Web/API/Navigator/userAgentData
 */
declare global {
  interface Navigator {
    userAgentData?: {
      brands: Array<{ brand: string; version: string }>; // ブラウザのブランド情報（Chrome、Edge等）
      mobile: boolean; // モバイル端末かどうかの真偽値
      platform: string; // OS情報（Windows、macOS、Android等）
    };
  }
}

/**
 * 現在の実行環境がモバイル端末かどうかを判定する
 * 複数の判定方法を段階的に適用して確実性を高める
 *
 * 判定順序:
 * 1. Client Hints API（最新・最も正確）
 * 2. User-Agentパターンマッチング（従来の方法・広範囲対応）
 * 3. タッチイベント検出（物理的特徴による判定）
 *
 * @returns {boolean} モバイル端末の場合はtrue、そうでなければfalse
 */
export function isMobile(): boolean {
  // サーバーサイドレンダリング（SSR）環境での安全な処理
  // navigatorオブジェクトが存在しない場合は非モバイルとして扱う
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent ?? '';
  const platform = navigator.userAgentData?.platform ?? ua;
  const pcRegex = /Win|Mac|Linux/i;
  const mobileRegex = /Mobile|Android|iPhone|iPad/i;
  const browserRegex = /Chrome|Firefox|Safari/i;

  // デスクトップOSとブラウザの判定
  // デスクトップOS（Windows、macOS、Linux）でデスクトップブラウザ（Chrome、Firefox、Safari）を使用している場合は非モバイルと判断
  const isDesktopOS = pcRegex.test(platform);
  const hasDesktopBrowser = browserRegex.test(ua) && !mobileRegex.test(ua);
  if (isDesktopOS && hasDesktopBrowser) {
    return false;
  }

  // 【方法1】Client Hints API による判定
  // 新しいブラウザAPIで、プライバシー保護されたデバイス情報を提供
  // Chrome 90+、Edge 90+ で利用可能
  if (typeof navigator.userAgentData?.mobile === 'boolean') {
    return navigator.userAgentData.mobile;
  }

  // 【方法2】User-Agent文字列による判定
  // 古いブラウザや非Chromiumブラウザでも動作する
  if (mobileRegex.test(ua)) {
    return true;
  }

  // 【方法3】タッチイベント対応による判定（フォールバック）
  // User-Agentが偽装されている場合やハイブリッドデバイスへの対応
  if (typeof window !== 'undefined') {
    // タッチイベントAPIの存在確認
    const hasTouchEvents = 'ontouchstart' in window;
    // マルチタッチポイントの対応数確認（0より大きければタッチ対応）
    const hasTouchPoints = (navigator.maxTouchPoints ?? 0) > 0;
    return hasTouchEvents || hasTouchPoints;
  }

  // 全ての判定方法で該当しない場合は非モバイルとして扱う
  return false;
}
